import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Textsms,
  CheckCircle,
  Error,
  Phone,
  Person,
  CalendarToday,
} from '@mui/icons-material';
import api from '../services/api';
import { format } from 'date-fns';

interface SMSNotification {
  id: number;
  collection_event_id: number;
  customer_phone: string;
  message_type: string;
  status: 'sent' | 'failed' | 'delivered';
  twilio_sid: string;
  sent_at: string;
  error_message?: string;
  order_number: string;
  customer_name: string;
  delivery_address: string;
  delivery_postcode: string;
  driver_name: string;
}

const SMSLog: React.FC = () => {
  const [logs, setLogs] = useState<SMSNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<'all' | 'sent' | 'failed'>('all');
  const [filterDate, setFilterDate] = useState<string>(new Date().toISOString().split('T')[0]); // Default to today
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    fetchLogs();
  }, [filterDate, filterStatus]);

  const fetchLogs = async () => {
    try {
      const params: any = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (filterDate) {
        params.date = filterDate;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      const response = await api.get('/sms-log', { params });
      setLogs(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching SMS logs:', error);
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchLogs();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      log.customer_name?.toLowerCase().includes(search) ||
      log.customer_phone?.includes(search) ||
      log.order_number?.toLowerCase().includes(search)
    );
  });

  // Calculate stats from filtered logs
  const today = new Date().toISOString().split('T')[0];
  const dateToCheck = filterDate || today;
  const stats = {
    total: filteredLogs.length,
    successful: filteredLogs.filter(log => log.status === 'sent' || log.status === 'delivered').length,
    failed: filteredLogs.filter(log => log.status === 'failed').length,
    sentToday: filteredLogs.filter(log => log.sent_at && log.sent_at.startsWith(dateToCheck)).length,
  };

  if (loading) {
    return <Typography>Loading SMS logs...</Typography>;
  }

  return (
    <Box>
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">SMS Notification Log</Typography>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#23a244', '&:hover': { backgroundColor: '#1e8e3a' } }}
            onClick={() => { fetchLogs(); }}
          >
            Refresh
          </Button>
        </Box>

        {/* Filters Row */}
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            type="date"
            label="Date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <TextField
            size="small"
            placeholder="Search customer, phone, order..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>

          {(searchTerm || filterStatus !== 'all' || filterDate !== new Date().toISOString().split('T')[0]) && (
            <Button
              size="small"
              onClick={() => {
                setFilterDate(new Date().toISOString().split('T')[0]); // Reset to today
                setSearchTerm('');
                setFilterStatus('all');
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Box>

      {/* Stats Cards - calculated from filtered data */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: '#e0f2fe' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.sentToday}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {filterDate === new Date().toISOString().split('T')[0] ? 'Sent Today' : 'On Selected Date'}
                  </Typography>
                </Box>
                <CalendarToday sx={{ fontSize: 40, color: '#0284c7' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: '#f9fafb' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total (Filtered)
                  </Typography>
                </Box>
                <Textsms sx={{ fontSize: 40, color: '#6b7280' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: '#dcfce7' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.successful}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Successful
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: '#10b981' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: stats.failed > 0 ? '#fee2e2' : '#f9fafb' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.failed}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Failed
                  </Typography>
                </Box>
                <Error sx={{ fontSize: 40, color: '#ef4444' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SMS Log Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
              <TableCell><strong>Date/Time</strong></TableCell>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell><strong>Phone</strong></TableCell>
              <TableCell><strong>Order</strong></TableCell>
              <TableCell><strong>Driver</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box py={4}>
                    <Textsms sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                    <Typography color="textSecondary">
                      No SMS notifications found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredLogs
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    {format(new Date(log.sent_at), 'dd/MM/yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                      {log.customer_name || 'Unknown'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                      {log.customer_phone}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {log.order_number || '-'}
                  </TableCell>
                  <TableCell>
                    {log.driver_name || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.status.toUpperCase()}
                      size="small"
                      color={getStatusColor(log.status) as any}
                    />
                    {log.error_message && (
                      <Typography variant="caption" color="error" display="block">
                        {log.error_message}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={filteredLogs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default SMSLog;
